/**!
 *  User favourites widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import Widget from "../../widget.js";
import "./favourites.scss";
import API from "Class/API";
import Auth from "Class/Auth";
import Fuse from "Class/Fuse";
import {ArrayClone, RandomToken, StripTags} from "Functions";
import ContentTypeField from "Components/UI/Field/ContentTypeField";
import IconItem from "Components/UI/IconItem";
import Link from "Components/UI/Link";
import Preview from "Components/Layout/Preview";
import Spinner from "Components/Feedback/Spinner";
import WidgetWrapper from "Components/UI/WidgetWrapper";

class WidgetFavourites extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: "Favourites",
                insert: true,
                label: "Title",
                reset: true,
                type: "text",
                translate: true
            },
            showFilter:
            {
                default: true,
                label: "Show content filter",
                type: "checkbox"
            },
            externalLink:
            {
                label: "Open contents in a new tab",
                type: "checkbox"
            }
        }, {
            backgroundColor: "transparent",
            textColor: "black"
        }, true);
        this.LoadToken = false;
        this.Mounted = false;
        this.Name = "Favourites";
        this.Translate =
        {
            filter: "All Content",
            more: "Show more",
            nomore: "No more favourites"
        };
        this.state =
        {
            content: [],
            done: false,
            filter: -1,
            error: false,
            loading: false,
            page: 0,
            translations: []
        };
    }

    /**
     * Load content on mount.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        this.Load();
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    /**
     * Output a content item.
     * @param object content - Content object.
     * @param integer index - Item index in list.
     * @return JSX - The content item.
     */

    Item = (content, index) =>
    {
        const {translations} = this.state;
        const {externalLink} = this.Content();
        const {description, id, preview_path, title, type} = content.favourite;
        const {description: translatedDescription, title: translatedTitle} = this.ShouldTranslate() ? translations[index] || {} : {};
        const Url = Fuse.ContentUrl(id, type);
        const PreviewUrl = preview_path ? Fuse.Url(preview_path.substr(1), false) : "";
        return (
            <Link
                className="WidgetFavouritesItem"
                key={`${index}.${id}`}
                href={Url}
                target={externalLink ? "_blank" : "_top"}
            >
                <Preview
                    className="ItemPreview"
                    content={type}
                    image={PreviewUrl}
                />
                <div className="ItemContent">
                    <div className="ItemName">{translatedTitle || title}</div>
                    <div className="ItemInfo">
                        {StripTags(translatedDescription || description)}
                    </div>
                </div>
            </Link>
        )
    }

    /**
     * Load Favourites content from the Fuse API.
     * @return void
     */

    Load = () =>
    {
        const {content, page, translations} = this.state;
        const {UserId} = Auth;
        const Page = page || 1;
        if (!UserId)
        {
            return;
        }
        const Token = this.LoadToken = RandomToken();
        this.setState({error: false, loading: true});
        Fuse.Request(`users/${UserId}/favourites`, {
            page: Page,
            swagger_request: "true"
        }, async (response) =>
        {
            if (!this.Mounted || Token !== this.LoadToken)
            {
                return;
            }
            const {last_page, items} = response || {};
            if (!items)
            {
                this.setState({error: true, loading: false});
            }
            else
            {
                const Content = ArrayClone(content).concat(items);
                const NewTranslations = await this.TranslateItems(items);
                const Translations = ArrayClone(translations).concat(NewTranslations);
                this.setState({
                    done: last_page,
                    content: Content,
                    loading: false,
                    page: Page + 1,
                    translations: Translations
                });
            }
        });
    }

    /**
     * Change content filter.
     * @param object e - The event object.
     * @param string filter - Content type.
     * @return void
     */

    SetFilter = (e, filter) =>
    {
        if (filter === this.state.filter)
        {
            return;
        }
        this.setState({filter});
    }

    /**
     * Translate items (if translation is active).
     * @param array items - Content items.
     * @return promise array - Translations.
     */

    TranslateItems = async (items) =>
    {
        const {translate} = this.props.content;
        if (!translate)
        {
            return [];
        }
        const Contents = items.map(({id, favourite: {description, title}}) => ({description, id, title}));
        const {translations} = await API.RequestAsync("translations/contents", {contents: Contents});
        return translations || [];
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, hover} = this.props;
        const {content, filter, done, loading} = this.state;
        const {title, showFilter} = WidgetContent;
        const CA = ["Widget", "WidgetFavourites", "White", "BorderRadius"];
        if (active)
        {
            CA.push("Active");
        }
        if (hover)
        {
            CA.push("Hover"); 
        }
        const Content = [];
        const Items = [];
        if (content && content.length)
        {
            content.forEach((c, i) =>
            {
                if (filter !== -1 && filter !== c.favourite.type)
                {
                    return;
                }
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className="WidgetSpinner"
                key="spinner"
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className="WidgetEmpty"
                key="empty"
            >No content</div>);
        }
        else
        {
            Content.push(<div
                className="WidgetFavouritesItems"
                key="items"
            >{Items}</div>);
            Content.push(<IconItem
                className="WidgetFavouritesButton"
                disabled={done}
                feather={done ? "StopCircle" : "PlusCircle"}
                key="fetch"
                label={done ? this.GetTranslation("nomore") : this.GetTranslation("more")}
                loading={loading}
                onClick={this.Load}
            />);
        }
        return (
            <div className={CA.join(" ")} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className="WidgetFavouritesWrapper"
                    title={title}
                    toolbar={showFilter ? <ContentTypeField
                        className="WidgetFavouritesFilter"
                        onChange={this.SetFilter}
                        placeholder={this.GetTranslation("filter")}
                        value={filter}
                    /> : ""} 
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetFavourites;